import type { ApiSchemas } from '@rental/contracts/client'

/**
 * S2: what an SDK envelope becomes before it leaves the browser: the
 * allow-listed parts of its error events and sessions, as the API's typed
 * report (`POST /v1/client-errors`). Read structurally, never trusting the
 * SDK's shape: anything unexpected is dropped, never passed on. The server
 * rebuilds and scrubs it again (packages/contracts, telemetry.ts).
 */
export type ClientErrorsBody = ApiSchemas['ClientErrorsRequest']
type Report = ApiSchemas['ClientErrorReport']
type Session = ApiSchemas['ClientSession']
type Exception = Report['exceptions'][number]
type Frame = Exception['frames'][number]

const MAX_ITEMS = 5
const MAX_EXCEPTIONS = 3
const MAX_FRAMES = 50
const HEX32 = /^[0-9a-f]{32}$/
const ROUTE_ID = /^(?:__root__|\/[A-Za-z0-9/$_.:()[\]-]{0,199})$/
const SESSION_STATUSES = ['ok', 'exited', 'crashed', 'abnormal'] as const

const record = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
const text = (value: unknown, max: number) =>
  typeof value === 'string' ? value.slice(0, max) : undefined
const count = (value: unknown) =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 10_000_000
    ? value
    : undefined

/** An instant as ISO 8601: the SDK writes seconds for events, strings for sessions. */
function instant(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value))
    return new Date(value * 1_000).toISOString()
  if (typeof value === 'string' && !Number.isNaN(Date.parse(value))) {
    return new Date(value).toISOString()
  }
  return undefined
}

function frame(value: unknown): Frame | null {
  const f = record(value)
  if (!f) return null
  const out: Frame = {
    filename: text(f.filename, 500) ?? text(f.abs_path, 500) ?? '<unknown>',
    inApp: typeof f.in_app === 'boolean' ? f.in_app : true,
  }
  const name = text(f.function, 200)
  if (name) out.function = name
  const lineno = count(f.lineno)
  if (lineno !== undefined) out.lineno = lineno
  const colno = count(f.colno)
  if (colno !== undefined) out.colno = colno
  return out
}

function exception(value: unknown): Exception | null {
  const e = record(value)
  if (!e) return null
  const mechanism = record(e.mechanism)
  const frames = record(e.stacktrace)?.frames
  return {
    type: text(e.type, 100) ?? 'Error',
    value: text(e.value, 2_000) ?? '',
    mechanism: {
      type: text(mechanism?.type, 50) ?? 'generic',
      handled: typeof mechanism?.handled === 'boolean' ? mechanism.handled : true,
    },
    frames: Array.isArray(frames)
      ? frames
          .slice(-MAX_FRAMES)
          .map(frame)
          .filter((f): f is Frame => f !== null)
      : [],
  }
}

/** An error event's allow-listed parts, or null when it is not an error with an id. */
export function reportFromEvent(value: unknown): Report | null {
  const event = record(value)
  const eventId = text(event?.event_id, 32)
  const timestamp = instant(event?.timestamp)
  const values = record(event?.exception)?.values
  if (!event || !eventId || !HEX32.test(eventId) || !timestamp || !Array.isArray(values)) {
    return null
  }
  const exceptions = values
    .slice(-MAX_EXCEPTIONS)
    .map(exception)
    .filter((e): e is Exception => e !== null)
  if (exceptions.length === 0) return null
  const route = text(record(event.tags)?.route, 200)
  const release = text(event.release, 100)
  return {
    eventId,
    timestamp,
    level: event.level === 'fatal' ? 'fatal' : event.level === 'warning' ? 'warning' : 'error',
    exceptions,
    tags: route !== undefined && ROUTE_ID.test(route) ? { route } : {},
    ...(release ? { release } : {}),
  }
}

/** A session's counts and times; never its `did`, IP address or user agent. */
export function sessionFrom(value: unknown): Session | null {
  const s = record(value)
  const sid = text(s?.sid, 32)
  const started = instant(s?.started)
  const timestamp = instant(s?.timestamp)
  const status = SESSION_STATUSES.find((name) => name === s?.status)
  const errors = count(s?.errors)
  if (!s || !sid || !HEX32.test(sid) || !started || !timestamp || !status || errors === undefined) {
    return null
  }
  const out: Session = { sid, init: s.init === true, started, timestamp, status, errors }
  if (typeof s.duration === 'number' && s.duration >= 0 && s.duration <= 31_536_000) {
    out.duration = s.duration
  }
  const release = text(record(s.attrs)?.release, 100)
  if (release) out.release = release
  return out
}

/** An SDK envelope (`[headers, [[itemHeaders, payload], …]]`) as the API's report. */
export function reportsFromEnvelope(envelope: unknown): ClientErrorsBody {
  const events: Report[] = []
  const sessions: Session[] = []
  const items = Array.isArray(envelope) && Array.isArray(envelope[1]) ? envelope[1] : []
  for (const item of items) {
    if (!Array.isArray(item)) continue
    const type = record(item[0])?.type
    if (type === 'event' && events.length < MAX_ITEMS) {
      const report = reportFromEvent(item[1])
      if (report) events.push(report)
    } else if (type === 'session' && sessions.length < MAX_ITEMS) {
      const session = sessionFrom(item[1])
      if (session) sessions.push(session)
    }
  }
  return { events, sessions }
}
